import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { clearSubmittalEvent } from "../../Redux/service/Submittals/SubmittalWebSocket";
import "./liveIR.css";

/**
 * Shows a toast when a submittal event arrives over the MaintBackend socket.
 *
 * Deliberately reuses the IR toast markup and liveIR.css rather than defining a second
 * look, so submittal and inspection-request notifications are visually identical. The
 * `ir-toast-*` classes already cover the success/error/info/warning variants and RTL.
 *
 * Nothing rendered socket events for submittals before this: LiveSnag.jsx is never
 * mounted and the bell (SideEvents.jsx) only fetches when the panel is opened.
 */

const eventConfig = (event) => ({
	created: {
		title: { en: "New Submittal", ar: "تقديم جديد" },
		type: "success",
	},
	submitted: {
		title: { en: "Submittal Resubmitted", ar: "تم إعادة تقديم التقديم" },
		type: "success",
	},
	qa_approved: {
		title: { en: "QA Approved", ar: "موافقة مراقب الجودة" },
		type: "success",
	},
	returned: {
		title: { en: "Returned to Contractor", ar: "أعيد إلى المقاول" },
		type: "warning",
	},
	approved: {
		title: { en: "Consultant Approved", ar: "موافقة الاستشاري" },
		type: "success",
	},
	rejected: {
		title: { en: "Submittal Rejected", ar: "تم رفض التقديم" },
		type: "error",
	},
	revision_required: {
		title: { en: "Revision Required", ar: "المراجعة مطلوبة" },
		type: "warning",
	},
})[event];

export default function LiveSubmittal() {
	const dispatch = useDispatch();
	const { latestSubmittalEvent, eventType } = useSelector(
		(state) => state.submittalWebSocket
	);
	const processedEventsRef = useRef(new Set());

	useEffect(() => {
		if (!latestSubmittalEvent || !eventType) return;

		// `updated` and `notification` ride along with one of the specific events above;
		// toasting them too would show every change twice.
		const config = eventConfig(eventType);
		if (!config) {
			dispatch(clearSubmittalEvent());
			return;
		}

		// The payload carries a fresh timestamp every time, so the identifier must be
		// built from stable fields or nothing would ever count as already processed.
		const eventIdentifier = `${latestSubmittalEvent.submittalId}-${eventType}`;
		if (processedEventsRef.current.has(eventIdentifier)) {
			dispatch(clearSubmittalEvent());
			return;
		}
		processedEventsRef.current.add(eventIdentifier);
		if (processedEventsRef.current.size > 50) {
			const arr = Array.from(processedEventsRef.current);
			processedEventsRef.current = new Set(arr.slice(-50));
		}

		const rtl = localStorage.getItem("lang") === "ar";
		const toastId = `${eventIdentifier}-${Date.now()}`;

		const CustomToast = () => (
			<div className={`ir-toast-container ${rtl ? "rtl" : "ltr"}`}>
				<button
					className="ir-custom-close-btn"
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						toast.dismiss(toastId);
					}}
					aria-label="Close"
					type="button"
				>
					✕
				</button>

				<div className="ir-toast-content">
					<div className="ir-toast-header">
						<span className="ir-toast-title">
							{config.title[rtl ? "ar" : "en"]} - {latestSubmittalEvent.submittalNumber}
						</span>
					</div>

					{latestSubmittalEvent.projectName && (
						<div className="ir-toast-project">
							<span className="ir-toast-project-icon">📍</span>
							<span className="ir-toast-project-label">
								{rtl ? "المشروع:" : "Project:"}
							</span>
							<span className="ir-toast-project-name">
								{latestSubmittalEvent.projectName}
							</span>
						</div>
					)}

					{latestSubmittalEvent.performedBy && (
						<div className="ir-toast-footer">
							<span className="ir-toast-submitted-label">
								{rtl ? "تم التقديم بواسطة:" : "Submitted by:"}
							</span>
							<span className="ir-toast-submitted-by">
								{latestSubmittalEvent.performedBy}
							</span>
						</div>
					)}
				</div>
			</div>
		);

		toast[config.type](<CustomToast />, {
			toastId,
			rtl,
			autoClose: 7000,
			position: "top-right",
			closeButton: false,
			pauseOnHover: true,
			draggable: true,
			hideProgressBar: false,
			className: "ir-toast-custom",
			bodyClassName: "ir-toast-body",
			progressClassName: "ir-toast-progress",
			closeOnClick: false,
		});

		dispatch(clearSubmittalEvent());
	}, [latestSubmittalEvent, eventType, dispatch]);

	return null;
}
